import { useRef, useEffect, useState, useCallback } from 'react';

import Pusher from 'pusher-js';

export const ConnectionState = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
};

export function usePusherChannel({
  key,
  cluster,
  channel: channelName,
  channelAuthUrl,
  channelAuthToken,
}) {
  const pusherChannelRef = useRef(null);
  // Hold the current token in a ref so a refresh updates the value the auth
  // request reads on the next (re)connect, without re-running the effect and
  // tearing down a live socket. headersProvider only reads it at reconnect
  // time, so mirroring it in an effect (rather than during render) is enough.
  const channelAuthTokenRef = useRef(channelAuthToken);
  useEffect(() => {
    channelAuthTokenRef.current = channelAuthToken;
  }, [channelAuthToken]);
  const [connectionStatus, setConnectionStatus] = useState(
    ConnectionState.CONNECTING,
  );
  const prevConnectionStatusRef = useRef(ConnectionState.CONNECTING);
  const [error, setError] = useState(null);
  const onStatusChangeSubscribers = useRef([]);

  const addOnStatusChangeSubscriber = useCallback(
    (fn) => {
      onStatusChangeSubscribers.current.push(fn);
    },
    [onStatusChangeSubscribers],
  );
  const handleError = (pusherError) => {
    setConnectionStatus(ConnectionState.ERROR);
    setError(pusherError);
  };

  useEffect(() => {
    const pusher = new Pusher(key, {
      cluster: cluster,
      channelAuthorization: {
        endpoint: channelAuthUrl,
        headersProvider: () => {
          return {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]')
              .content,
            'X-Channel-Auth-Token': channelAuthTokenRef.current,
          };
        },
      },
    });

    // https://pusher.com/docs/channels/using_channels/connection/#available-states
    pusher.connection.bind('connecting', () => {
      setConnectionStatus(ConnectionState.CONNECTING);
    });
    pusher.connection.bind('connected', () => {
      setConnectionStatus(ConnectionState.CONNECTED);
    });
    pusher.connection.bind('unavailable', () => handleError());
    pusher.connection.bind('error', (generalError) =>
      handleError(generalError),
    );

    pusherChannelRef.current = pusher.subscribe(channelName);
    pusherChannelRef.current.bind(
      'pusher:subscription_error',
      (subscriptionError) => {
        handleError(subscriptionError);
      },
    );

    // The token is read from a ref, so the effect only re-runs when the
    // connection identity changes; disconnect fully on teardown.
    return () => pusher.disconnect();
  }, [key, cluster, channelName]);

  useEffect(() => {
    onStatusChangeSubscribers.current.forEach((fn) =>
      fn(connectionStatus, prevConnectionStatusRef.current),
    );
    prevConnectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  return {
    channel: pusherChannelRef.current,
    connectionStatus,
    error,
    addOnStatusChangeSubscriber,
  };
}
